import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AppPaperTitle from 'src/app/common/components/AppPaperTitle';
import { PruTableHeader } from 'src/app/common/components/Table/PruTableHeader';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import useEventCallback from 'src/app/common/hook/useEventCallback';
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from 'src/app/modules/OpmPd/constants/common.constant';
import TableHeaderFilterLabel from '../../../../../../../common/components/TableHeaderFilterLabel';
import { useBatchFilesSubmitPageContext } from '../../BatchFilesSubmitPage.context';
import AssignAgentButton from './components/AssignAgentButton';
import useStyles from './PolicyTable.styles';
import ExportButton from 'src/app/modules/OpmPd/components/ExportButton/ExportButton';
import _isEqual from 'lodash/isEqual';

type PolicyTableProps<Item extends Record<string, any> = Record<string, any>> = {
  items?: Item[];
  rowsPerPageOptions?: number[];
};

export type QualifiedAgent = { agentCode: string; fullname: string; label: string; policyNumbers: string[] };

const EMPTY_FILTER_VALUE = '(Empty)';

const withEmpty = (sorted: string[]): string[] => [EMPTY_FILTER_VALUE, ...sorted];

const defaultColumnFilters: Record<ColumnKey, string[]> = {
  policyNumber: [],
  policyStatusCode: [],
  currentAgentCode: [],
  soldUnit: [],
  importedAgent: [],
  suggestedAgent: [],
  suggestedAgentName: [],
  selectedOtherAgentReason: [],
  displaySelectedAgent: [],
  displaySelectedAgentName: [],
  selectedOtherQualifiedAgentLabel: [],
  reasonImportedAgentIsNotQualified: [],
};

type ColumnKey =
  | 'policyNumber'
  | 'policyStatusCode'
  | 'currentAgentCode'
  | 'soldUnit'
  | 'importedAgent'
  | 'suggestedAgent'
  | 'suggestedAgentName'
  | 'displaySelectedAgent'
  | 'displaySelectedAgentName'
  | 'reasonImportedAgentIsNotQualified'
  | 'selectedOtherQualifiedAgentLabel'
  | 'selectedOtherAgentReason';

const PolicyTable = (props: PolicyTableProps) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const { rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS } = props;
  const { classes } = useStyles();
  const { bdmBatchFile, formPolicies, setPolicyFieldValue, removeFormPolicy } = useBatchFilesSubmitPageContext();

  const [columnFilters, setColumnFilters] = useState<Record<ColumnKey, string[]>>(defaultColumnFilters);

  const [selectedPolicyIndexes, setSelectedPolicyIndexes] = useState<number[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0] ?? DEFAULT_ROWS_PER_PAGE_OPTIONS[0]);

  const policies = useMemo(() => formPolicies.filter((policy) => !policy.isDeleted), [formPolicies]);
  const [filteredItems, setFilteredItems] = useState([...policies]);

  const qualifiedAgents = useMemo(() => {
    const agentsMap: Record<string, QualifiedAgent> = {};
    (formPolicies ?? []).forEach((policy) => {
      if (policy.isDeleted) return;
      policy.qualifiedAgents.forEach((agent) => {
        if (!agentsMap[agent.agentCode]) {
          agentsMap[agent.agentCode] = { ...agent, label: `${agent.agentCode} - ${agent.fullname}`, policyNumbers: [] };
        }
        const alreadyAdded = agentsMap[agent.agentCode].policyNumbers.some((p) => p === policy.policyNumber);
        if (!alreadyAdded) {
          agentsMap[agent.agentCode].policyNumbers.push(policy.policyNumber);
        }
      });
    });
    return Object.values(agentsMap);
  }, [formPolicies]);

  const columnOptions = useMemo<Record<ColumnKey, string[]>>(() => {
    return {
      policyNumber: withEmpty([...new Set(policies.map((i) => String(i.policyNumber ?? '')))].filter(Boolean).sort()),
      policyStatusCode: withEmpty(
        [...new Set(policies.map((i) => String(i.policyStatusCode ?? '')))].filter(Boolean).sort(),
      ),
      currentAgentCode: withEmpty(
        [...new Set(policies.map((i) => String(i.currentAgentCode ?? '')))].filter(Boolean).sort(),
      ),
      soldUnit: withEmpty([...new Set(policies.map((i) => String(i.soldUnit ?? '')))].filter(Boolean).sort()),
      importedAgent: withEmpty([...new Set(policies.map((i) => String(i.importedAgent ?? '')))].filter(Boolean).sort()),
      suggestedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.suggestedAgent ?? '')))].filter(Boolean).sort(),
      ),
      suggestedAgentName: withEmpty(
        [...new Set(policies.map((i) => String(i.suggestedAgentName ?? '')))].filter(Boolean).sort(),
      ),
      selectedOtherQualifiedAgentLabel: withEmpty(
        [...new Set(policies.map((i) => String(i.selectedOtherQualifiedAgent?.label ?? '')))].filter(Boolean).sort(),
      ),
      displaySelectedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.displaySelectedAgent ?? '')))].filter(Boolean).sort(),
      ),
      displaySelectedAgentName: withEmpty(
        [...new Set(policies.map((i) => String(i.displaySelectedAgentName ?? '')))].filter(Boolean).sort(),
      ),
      selectedOtherAgentReason: withEmpty(
        [...new Set(policies.map((i) => String(i.selectedOtherAgentReason ?? '')))].filter(Boolean).sort(),
      ),
      reasonImportedAgentIsNotQualified: withEmpty(
        [...new Set(policies.flatMap((i) => i.reasonImportedAgentIsNotQualified ?? []))].filter(Boolean).sort(),
      ),
    };
  }, [formPolicies]);

  const changeFilteredItems = useEventCallback((payload?: { columnFilters: typeof columnFilters }) => {
    const { columnFilters } = payload || {};

    const matchStr = (filter: string[] | null | undefined, val: string | null | undefined): boolean => {
      if (['null', 'undefined'].includes(typeof filter) || !filter?.length) return true;
      const s = String(val ?? '');
      return filter!.some((f) => (f === EMPTY_FILTER_VALUE ? !s : f === s));
    };
    const matchArr = (filter: string[] | null | undefined, vals: string[] | null | undefined): boolean => {
      if (['null', 'undefined'].includes(typeof filter) || !filter?.length) return true;
      return filter!.some((f) => (f === EMPTY_FILTER_VALUE ? !(vals ?? []).length : (vals ?? []).includes(f)));
    };
    setFilteredItems(
      policies.filter(
        (item) =>
          matchStr(columnFilters?.policyNumber, item.policyNumber) &&
          matchStr(columnFilters?.policyStatusCode, item.policyStatusCode) &&
          matchStr(columnFilters?.currentAgentCode, item.currentAgentCode) &&
          matchStr(columnFilters?.soldUnit, item.soldUnit) &&
          matchStr(columnFilters?.importedAgent, item.importedAgent) &&
          matchStr(columnFilters?.suggestedAgent, item.suggestedAgent) &&
          matchStr(columnFilters?.suggestedAgentName, item.suggestedAgentName) &&
          matchStr(columnFilters?.displaySelectedAgent, item.displaySelectedAgent) &&
          matchStr(columnFilters?.displaySelectedAgentName, item.displaySelectedAgentName) &&
          matchStr(columnFilters?.selectedOtherQualifiedAgentLabel, item.selectedOtherQualifiedAgent?.label) &&
          matchStr(columnFilters?.selectedOtherAgentReason, item.selectedOtherAgentReason) &&
          matchArr(columnFilters?.reasonImportedAgentIsNotQualified, item.reasonImportedAgentIsNotQualified),
      ),
    );
  });

  const makeFilterHandler = (col: ColumnKey) => (selected: string[]) => {
    setPage(0);
    setSelectedPolicyIndexes([]);
    const validSelected = selected.filter((s) => columnOptions[col].includes(s));
    const newColumnFilters = {
      ...columnFilters,
      [col]: validSelected.length === 0 || validSelected.length === columnOptions[col].length ? [] : validSelected,
    };
    setColumnFilters(newColumnFilters);
    changeFilteredItems({ columnFilters: newColumnFilters });
  };

  const paginatedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleRow = (index: number) => {
    setSelectedPolicyIndexes((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const handleToggleAllOnPage = () => {
    const pageIndices = paginatedItems.map((item) => item.index);
    const allSelected = pageIndices.every((idx) => selectedPolicyIndexes.includes(idx));
    if (allSelected) {
      setSelectedPolicyIndexes((prev) => prev.filter((i) => !pageIndices.includes(i)));
    } else {
      setSelectedPolicyIndexes((prev) => [...new Set([...prev, ...pageIndices])]);
    }
  };

  const handleDeleteSelected = () => {
    removeFormPolicy(selectedPolicyIndexes);
    setSelectedPolicyIndexes([]);
    setFilteredItems((prev) => prev.filter((item) => !selectedPolicyIndexes.includes(item.index)));
  };

  const handleClearFilters = () => {
    setColumnFilters(defaultColumnFilters);
    changeFilteredItems({ columnFilters: defaultColumnFilters });
    setPage(0);
  };

  const paginatedItemPolicyNumbers = useMemo(() => {
    return paginatedItems?.map((paginatedItem) => String(paginatedItem.policyNumber)) || [];
  }, [paginatedItems]);
  const paginatedItemPolicyNumbersRef = useRef(paginatedItemPolicyNumbers);

  useEffect(() => {
    if (_isEqual(paginatedItemPolicyNumbersRef.current, paginatedItemPolicyNumbers)) {
      return;
    }
    setSelectedPolicyIndexes([]);
    paginatedItemPolicyNumbersRef.current = paginatedItemPolicyNumbers;
  }, [paginatedItemPolicyNumbers]);

  return (
    <Paper elevation={0} variant="elevation" className={classes.root}>
      <AppPaperTitle>
        {Translation('opm_pd.validPolicies')}
        <Box display="flex" flex={1} alignItems="center" justifyContent="flex-end" gap={1}>
          <ExportButton module="bdmBatchFilePolicies" batchId={bdmBatchFile?.id} />
          <AssignAgentButton qualifiedAgents={qualifiedAgents} filteredItems={filteredItems} />
          <Button
            variant="outlined"
            disabled={Object.values(columnFilters).every((v) => v.length === 0)}
            onClick={handleClearFilters}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {Translation('opm_pd.clearFilters')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={selectedPolicyIndexes.length === 0}
            onClick={handleDeleteSelected}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {Translation('opm_pd.delete')}
            {selectedPolicyIndexes.length > 0 ? ` (${selectedPolicyIndexes.length})` : ''}
          </Button>
        </Box>
      </AppPaperTitle>
      <div>
        <TableContainer sx={{ maxHeight: `calc(100dvh - 220px)` }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <PruTableHeader padding="checkbox">
                  <Checkbox
                    checked={
                      paginatedItems.length > 0 &&
                      paginatedItems.every((item) => selectedPolicyIndexes.includes(item.index))
                    }
                    indeterminate={
                      paginatedItems.some((item) => selectedPolicyIndexes.includes(item.index)) &&
                      !paginatedItems.every((item) => selectedPolicyIndexes.includes(item.index))
                    }
                    onChange={handleToggleAllOnPage}
                    sx={{
                      color: 'white',
                      '&.Mui-checked': { color: 'white' },
                      '&.MuiCheckbox-indeterminate': { color: 'white' },
                    }}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.policyNumber')}
                    options={columnOptions.policyNumber}
                    selectedOptions={columnFilters.policyNumber ?? []}
                    onSelectionChange={makeFilterHandler('policyNumber')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.policyStatusCode')}
                    options={columnOptions.policyStatusCode}
                    selectedOptions={columnFilters.policyStatusCode ?? []}
                    onSelectionChange={makeFilterHandler('policyStatusCode')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.currentAgentCode')}
                    options={columnOptions.currentAgentCode}
                    selectedOptions={columnFilters.currentAgentCode ?? []}
                    onSelectionChange={makeFilterHandler('currentAgentCode')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.soldUnit')}
                    options={columnOptions.soldUnit}
                    selectedOptions={columnFilters.soldUnit ?? []}
                    onSelectionChange={makeFilterHandler('soldUnit')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.importedAgent')}
                    options={columnOptions.importedAgent}
                    selectedOptions={columnFilters.importedAgent ?? []}
                    onSelectionChange={makeFilterHandler('importedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.suggestedAgent')}
                    options={columnOptions.suggestedAgent}
                    selectedOptions={columnFilters.suggestedAgent ?? []}
                    onSelectionChange={makeFilterHandler('suggestedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.suggestedAgentName')}
                    options={columnOptions.suggestedAgentName}
                    selectedOptions={columnFilters.suggestedAgentName ?? []}
                    onSelectionChange={makeFilterHandler('suggestedAgentName')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.otherQualifiedAgent')}
                    options={columnOptions.selectedOtherQualifiedAgentLabel}
                    selectedOptions={columnFilters.selectedOtherQualifiedAgentLabel ?? []}
                    onSelectionChange={makeFilterHandler('selectedOtherQualifiedAgentLabel')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.selectedAgent')}
                    options={columnOptions.displaySelectedAgent}
                    selectedOptions={columnFilters.displaySelectedAgent ?? []}
                    onSelectionChange={makeFilterHandler('displaySelectedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.selectedAgentName')}
                    options={columnOptions.displaySelectedAgentName}
                    selectedOptions={columnFilters.displaySelectedAgentName ?? []}
                    onSelectionChange={makeFilterHandler('displaySelectedAgentName')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.reasonForSelectingAnotherAgent')}
                    options={columnOptions.selectedOtherAgentReason}
                    selectedOptions={columnFilters.selectedOtherAgentReason ?? []}
                    onSelectionChange={makeFilterHandler('selectedOtherAgentReason')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={Translation('opm_pd.reasonForImportingAgentNotQualified')}
                    options={columnOptions.reasonImportedAgentIsNotQualified}
                    selectedOptions={columnFilters.reasonImportedAgentIsNotQualified ?? []}
                    onSelectionChange={makeFilterHandler('reasonImportedAgentIsNotQualified')}
                  />
                </PruTableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((item, index) => {
                const hasSelectedOtherQualifiedAgent = !!formPolicies[item.index]?.selectedOtherQualifiedAgent;
                return (
                  <TableRow
                    key={item.policyNumber ?? index}
                    hover
                    selected={selectedPolicyIndexes.includes(item.index)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPolicyIndexes.includes(item.index)}
                        onChange={() => handleToggleRow(item.index)}
                      />
                    </TableCell>
                    <TableCell>{item.policyNumber ?? '-'}</TableCell>
                    <TableCell>{item.policyStatusCode ?? '-'}</TableCell>
                    <TableCell>{item.currentAgentCode ?? '-'}</TableCell>
                    <TableCell>{item.soldUnit ?? '-'}</TableCell>
                    <TableCell>{item.importedAgent ?? '-'}</TableCell>
                    <TableCell>{item.suggestedAgent ?? '-'}</TableCell>
                    <TableCell>{item.suggestedAgentName ?? '-'}</TableCell>
                    <TableCell>
                      <Autocomplete
                        options={item.qualifiedAgents ?? []}
                        value={formPolicies[item.index]?.selectedOtherQualifiedAgent ?? null}
                        onChange={(_, value) => {
                          setPolicyFieldValue(
                            item.index,
                            'selectedOtherQualifiedAgent',
                            value
                              ? {
                                  ...value,
                                  label: `${value?.agentCode} - ${value?.fullname}`,
                                }
                              : null,
                          );
                          setPolicyFieldValue(
                            item.index,
                            'displaySelectedAgent',
                            value ? value.agentCode : item.selectedAgent,
                          );
                          setPolicyFieldValue(
                            item.index,
                            'displaySelectedAgentName',
                            value ? value.fullname : item.selectedAgentName,
                          );
                        }}
                        renderInput={(params) => <TextField {...params} variant="standard" sx={{ width: 160 }} />}
                        getOptionLabel={(option) => `${option.agentCode} - ${option.fullname}`}
                      />
                    </TableCell>
                    <TableCell>{formPolicies[item.index]?.displaySelectedAgent ?? '-'}</TableCell>
                    <TableCell>{formPolicies[item.index]?.displaySelectedAgentName ?? '-'}</TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        sx={{ width: 160 }}
                        value={formPolicies[item.index]?.selectedOtherAgentReason ?? ''}
                        disabled={!hasSelectedOtherQualifiedAgent}
                        onChange={(event) =>
                          setPolicyFieldValue(item.index, 'selectedOtherAgentReason', event.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {item.reasonImportedAgentIsNotQualified?.map((reason) => (
                          <Chip key={reason} label={reason} size="small" />
                        )) ?? '-'}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={18} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {Translation('opm_pd.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </Paper>
  );
};

export default PolicyTable;
